import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Plus, Trash2, Save, Globe } from "lucide-react";
import { api, adminAuthHeaders } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-[#1f3a8a] focus:ring-1 focus:ring-[#1f3a8a] outline-none font-mono";

export default function SecurityPanel() {
  const [allowAll, setAllowAll] = useState(true);
  const [list, setList] = useState([]);
  const [newIp, setNewIp] = useState("");
  const [currentIp, setCurrentIp] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const myRole = localStorage.getItem("rm_admin_role") || "admin";
  const canEdit = myRole === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/security", { headers: adminAuthHeaders() });
        setAllowAll(!!data.allow_all_ips);
        setList(data.ip_whitelist || []);
        setCurrentIp(data.current_ip || "");
      } catch {
        toast.error("Nie udało się wczytać ustawień bezpieczeństwa");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addIp = () => {
    const v = newIp.trim();
    if (!v) return;
    if (list.includes(v)) return toast.error("Ten adres IP jest już na liście");
    setList([...list, v]);
    setNewIp("");
  };

  const removeIp = (ip) => setList(list.filter((x) => x !== ip));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/security", { allow_all_ips: allowAll, ip_whitelist: list }, { headers: adminAuthHeaders() });
      toast.success("Zapisano ustawienia bezpieczeństwa");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Wczytywanie…</div>;

  return (
    <div data-testid="security-panel" className="p-6 space-y-6">
      <section className="rounded-xl border border-slate-200 p-5 bg-white">
        <h3 className="font-bold text-slate-800 flex items-center gap-2"><Shield size={18} className="text-[#1f3a8a]" /> Dostęp do eksportu danych</h3>
        <p className="text-sm text-slate-500 mt-2">
          Ogranicz, kto może pobierać dane przez <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">/api/export/orders</code>.
          Klucz API jest wymagany zawsze — biała lista IP to dodatkowa warstwa ochrony.
        </p>
        {currentIp && (
          <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
            <Globe size={14} /> Twój obecny IP: <code data-testid="current-ip" className="font-mono text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{currentIp}</code>
          </div>
        )}

        <label className="mt-5 flex items-center gap-3 cursor-pointer">
          <input
            data-testid="allow-all-ips"
            type="checkbox"
            checked={allowAll}
            onChange={(e) => setAllowAll(e.target.checked)}
            disabled={!canEdit}
            className="w-4 h-4"
          />
          <span className="text-sm text-slate-800 font-semibold">Zezwól wszystkim adresom IP (biała lista wyłączona)</span>
        </label>

        <div className={`mt-5 ${allowAll ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Biała lista adresów IP</div>
          <div className="flex gap-2">
            <input
              data-testid="new-ip-input"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIp())}
              className={inputCls}
              placeholder="np. 51.38.128.98"
              disabled={!canEdit}
            />
            <button data-testid="add-ip-btn" type="button" onClick={addIp} disabled={!canEdit} className="inline-flex items-center gap-1 bg-[#1f3a8a] hover:bg-[#15296b] text-white font-semibold text-sm px-4 rounded-lg disabled:opacity-40">
              <Plus size={14} /> Dodaj
            </button>
          </div>
          {list.length === 0 && !allowAll && (
            <p className="text-xs text-red-500 mt-3">Uwaga: biała lista jest pusta — nikt (poza samym serwerem) nie pobierze danych.</p>
          )}
          <ul className="mt-4 space-y-2">
            {list.map((ip) => (
              <li key={ip} data-testid={`ip-row-${ip}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <code className="font-mono text-slate-800 text-sm">{ip}</code>
                {canEdit && <button onClick={() => removeIp(ip)} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>}
              </li>
            ))}
          </ul>
        </div>

        {canEdit && (
          <div className="mt-6 flex justify-end">
            <button data-testid="save-security" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white font-bold px-5 py-2.5 rounded-full transition disabled:opacity-60">
              <Save size={16} /> {saving ? "Zapisywanie…" : "Zapisz ustawienia"}
            </button>
          </div>
        )}
        {!canEdit && <p className="text-xs text-slate-400 mt-4">Tylko konto administratora może zmieniać te ustawienia.</p>}
      </section>
    </div>
  );
}
